import {
    Avatar,
    AvatarGroup,
    Box,
    Card,
    CardActions,
    CardContent,
    Chip,
    IconButton,
    Stack,
    Typography,
    useTheme
} from '@mui/material';
import { Roles } from '@utils/roleUtils';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { Link, useParams } from 'react-router-dom';

export const OrganizationUserCard = ({
    item,
    actionButtons,
    onClick,
    showSubordinates,
}: {
    item: any;
    onClick?: any;
    actionButtons?: any;
    showSubordinates?: boolean;
}) => {
    const {id } = useParams<{ id: string; }>();
    const theme = useTheme();
    const styles = getStyles(theme);
    const enableLink = item.role === Roles.ADMIN_PRO && !onClick;
    const roleLabel = item?.role === Roles.ADMIN_PRO ? 'Admin' : item?.role === Roles.ACCOUNTANT_PRO ? 'Comptable' : 'Membre';
    const roleColor = item?.role === Roles.ADMIN_PRO ? 'primary' : item?.role === Roles.ACCOUNTANT_PRO ? 'warning' : 'secondary';
    const roleBorderColor = item?.role === Roles.ADMIN_PRO ? theme.palette.primary.main : item?.role === Roles.ACCOUNTANT_PRO ? theme.palette.warning.main : theme.palette.secondary.main;
    return (
        <Card
            variant="outlined"
            sx={{
                height: '100%',
                minHeight: 216,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 4,
            }}>
            <CardContent
                sx={{
                    px: 2,
                    pb: 0,
                }}>
                <Stack
                    flexDirection={'row'}
                    alignItems={'flex-start'}
                    justifyContent={'flex-start'}
                    gap={'24px'}
                    sx={{ width: '100%' }}>
                    <Stack alignItems="center" sx={{ minWidth: '72px' }}>
                        <Avatar
                            src={item.file?.url}
                            sx={{
                                bgcolor: item.color,
                                fontWeight: 700,
                                width: '55px',
                                height: '55px',
                                borderWidth: '3px',
                                borderStyle: 'solid',
                                borderColor: roleBorderColor,
                            }}>
                            {`${item?.firstName[0] ?? ''} ${item?.lastName[0] ?? ''}`.toUpperCase()}
                        </Avatar>
                        <Chip
                            label={roleLabel}
                            color={roleColor}
                            size="small"
                            sx={{
                                mt: '-8px',
                                zIndex: 1,
                                fontWeight: 700,
                                minWidth: '64px',
                            }}
                        />
                    </Stack>
                    <Stack
                        component={enableLink ? Link : undefined}
                        to={enableLink ? `/companies/${id}/users/${item._id}` : undefined}
                        onClick={onClick}
                        direction="row"
                        alignItems="flex-start">
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                flexWrap: 'wrap',
                                width: '100%',
                                overflowWrap: 'anywhere',
                            }}>
                            <Typography sx={styles.userName}>{`${item?.firstName ?? ''} ${item?.lastName ?? ''
                                }`}</Typography>
                            <Typography sx={styles.userEmail}>{item?.email ?? ''}</Typography>
                        </Box>
                    </Stack>
                    <Stack alignSelf={'flex-start'} sx={{marginLeft: "auto"}}>{actionButtons?.(item)}</Stack>
                </Stack>
            </CardContent>
            <CardContent
                sx={{
                    px: 2,
                    pb: 0
                }}>
                <Stack flexDirection={'row'} alignItems={'flex-start'} justifyContent={'flex-start'} gap={1}>
                    <Stack gap={0.5}>
                        <Typography sx={styles.label}>Date d'ajout</Typography>
                        <Typography sx={styles.value} width={140}>
                            {!isEmpty(item?.createdDate) ? moment(item?.createdDate).format('DD MMM YYYY') : ''}
                        </Typography>
                    </Stack>
                    <Stack gap={0.5} alignItems={'flex-start'}>
                        <Typography sx={styles.label}>Statut de l'utilisateur</Typography>
                        <Chip
                            label={item?.isMemberActive ? 'Active' : 'Désactivé'}
                            color={item?.isMemberActive ? 'success' : 'error'}
                        />
                    </Stack>
                    <Stack gap={0.5} alignItems={'flex-start'}>
                        <Typography sx={styles.label}>Offres d'emploi</Typography>
                        <Typography 
                            component={Link}
                            to={`/companies/${id}/job-offers?assigneeId=${item._id}`}
                            sx={{
                                ...styles.value,
                                textDecoration: item?.jobOfferStatsOrgUser?.jobs?.total > 0 ? 'underline' : 'none',
                            }} width={140}>
                           {item?.jobOfferStatsOrgUser?.jobs?.total ?? 0}
                        </Typography>
                    </Stack>
                </Stack>
            </CardContent>
            {item?.role === Roles.ADMIN_PRO && <CardActions
                sx={{
                    bgcolor: theme.palette.transparentPurple.main,
                    flexDirection: 'space-between',
                    alignItems: 'center',
                    height: 40,
                    marginTop: 'auto',
                }}>
                <Stack gap={1} flexDirection="row" alignItems={'center'} justifyContent={'space-between'}>
                    <AvatarGroup
                        max={3}
                        total={item.totalSubordinates}
                        sx={{
                            '& .MuiAvatar-root': {
                                width: 24,
                                height: 24,
                                fontSize: 12,
                            },
                        }}>
                        {item.subordinates?.map(subordinate => {
                            return (
                                <Avatar
                                    key={subordinate?._id}
                                    src={subordinate.file?.[0]?.url}
                                    sx={{
                                        bgcolor: theme.palette.neutralDarkGray.main,
                                    }}>
                                    {`${subordinate.firstName[0]}${subordinate.lastName[0] ?? ''}`.toUpperCase()}
                                </Avatar>
                            );
                        })}
                    </AvatarGroup>
                    {/* X members / admin created in french*/}
                    {item.totalSubordinates > 0 ? (
                        <Typography>
                            {item.totalSubordinates} {item.totalSubordinates > 1 ? 'membres' : 'membre'}
                        </Typography>
                    ) :  (
                        <Typography>
                            {/* no member */}
                            Aucun membre
                        </Typography>
                    )}
                </Stack>
                {showSubordinates && (
                    <IconButton
                        component={enableLink ? Link : undefined}
                        to={enableLink ?`/companies/${id}/users/${item._id}` : undefined}
                        sx={{ marginLeft: 'auto' }}>
                        <Box component="i" className="fi-br-arrow-small-right" sx={{ color: 'primary.main' }} />
                    </IconButton>
                )}
            </CardActions>}
        </Card>
    );
};

const getStyles = (theme: any) => ({
    // HEADER
    header: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        background: theme.palette.neutralBlack.main,
        color: theme.palette.neutralWhite.main,
    },
    headerTitle: {
        fontFamily: 'LEMON MILK Pro',
        fontSize: '32px',
        fontWeight: 700,
        marginBottom: '10.5px',
    },
    subRow: {
        flexDirection: 'row',
        gap: '10px',
    },
    headerEmail: {
        color: theme.palette.neutralWhite.main,
        textAlign: 'center',
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '17px',
        fontWeight: 500,
    },
    headerRole: {
        backgroundColor: theme.palette.neutralWhite.main,
        color: theme.palette.neutralBlack.main,
        borderRadius: '16px',
        padding: '4px 12px',
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '14px',
        fontWeight: 600,
    },
    // NAVIGATION
    navigation: {
        display: 'flex',
        flexDirection: 'column',
        padding: '40px 49px 40px 32px',
        alignItems: 'flex-start',
        borderRadius: '16px',
        background: theme.palette.neutralWhite.main,
        height: 'fit-content',
        width: '336px',
        boxSizing: 'border-box',
        gap: '8px',
    },
    navigationItem: {
        padding: '12px',
        width: '100%',
        borderRadius: '12px',
        color: theme.palette.text.primary,
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '16px',
        textDecoration: 'none',
        fontWeight: 500,
    },
    navigationItemSelected: {
        padding: '12px',
        width: '100%',
        borderRadius: '12px',
        background: theme.palette.background.default,
        color: theme.palette.text.primary,
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '16px',
        textDecoration: 'none',
        fontWeight: 700,
    },
    navigationMobile: {
        display: 'flex',
        flexDirection: 'column',
        padding: '18px',
        borderRadius: '16px',
        background: theme.palette.neutralBlack.main,
        height: 'fit-content',
        width: '90vw',
        boxSizing: 'border-box',
        gap: '8px',
    },
    mobileNavigationItemSelected: {
        padding: '12px 8px',
        borderRadius: '12px',
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '16px',
        textDecoration: 'none',
        fontWeight: 600,
        color: theme.palette.neutralWhite.main,
        left: 0,
        right: 0,
    },
    mobileNavigationItem: {
        padding: '12px 8px',
        borderRadius: '12px',
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '16px',
        textDecoration: 'none',
        fontWeight: 500,
        color: theme.palette.neutralWhite.main,
        left: 0,
        right: 0,
    },
    container: {
        flexDirection: 'row',
        gap: '23px',
        marginTop: '30px',
    },
    // CONTENT
    headerSection: {
        alignItems: { xs: 'flex-start', md: 'center' },
        marginBottom: '28px',
        justifyContent: 'space-between',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: '16px' },
    },
    title: {
        color: theme.palette.text.primary,
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '28px',
        fontWeight: 700,
    },
    subtitle: {
        color: theme.palette.text.secondary,
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '16px',
        fontWeight: 400,
    },
    contentContainer: {
        borderRadius: '16px',
        background: theme.palette.background.paper,
        padding: '40px',
        overflow: 'auto',
        boxShadow: '0px 4px 80px 0px rgba(112, 144, 176, 0.14)',
        flex: 1,
    },
    tableContainer: {
        marginTop: '5px',
        marginBottom: '10px',
        borderRadius: '16px',
        border: `1px solid ${theme.palette.border.main}`,
        overflow: 'auto',
    },
    userItem: {
        borderTop: `1px solid ${theme.palette.border.main}`,
        borderBottom: `1px solid ${theme.palette.border.main}`,
    },
    label: {
        color: theme.palette.text.secondary,
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '14px',
        fontWeight: 500,
    },
    value: {
        color: theme.palette.text.primary,
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '20px',
        fontWeight: 500,
    },
    userName: {
        color: theme.palette.text.primary,
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '20px',
        fontWeight: 600,
    },
    userEmail: {
        color: theme.palette.text.secondary,
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '16px',
        fontWeight: 400,

        // 2 lines
        display: '-webkit-box',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
});

export default OrganizationUserCard;
